import React, { useState, useEffect, useContext } from "react";
import {
  motion,
  useTransform,
  useScroll,
  AnimatePresence,
} from "framer-motion";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import EnquiryPopup from "../components/EnquiryPopup";
import { PackagesContext } from "../context/PackagesContext";

// Snowflake Component
const Snowflake = ({ left, delay, duration }) => (
  <motion.div
    className="snowflake"
    initial={{ y: -50, opacity: 0 }}
    animate={{ y: "100vh", opacity: [1, 1, 0] }}
    transition={{
      duration,
      repeat: Infinity,
      delay,
      ease: "linear",
    }}
    style={{
      position: "fixed",
      top: 0,
      left: `${left}%`,
      fontSize: "1.2rem",
      color: "#fff",
      zIndex: 1,
      pointerEvents: "none",
    }}
  >
    ❆
  </motion.div>
);

const Home = ({ buttonText = "Get Free Quote" }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [currentPackageIndex, setCurrentPackageIndex] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 400], [0, 100]);
  const { packages } = useContext(PackagesContext);
  const [shuffledPackages, setShuffledPackages] = useState([]);
  const [direction, setDirection] = useState(1);
  console.log("package", packages)

  useEffect(() => {
    if (packages.length > 0 && shuffledPackages.length === 0) {
      const shuffled = [...packages].sort(() => 0.5 - Math.random());
      setShuffledPackages(shuffled);
      setCurrentPackageIndex(0);
    }
  }, [packages, shuffledPackages]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setShowPopup(true);
  }, []);

  useEffect(() => {
    if (shuffledPackages.length > 1) {
      const interval = setInterval(() => {
        setCurrentPackageIndex((prevIndex) => {
          let newIndex;
          do {
            newIndex = Math.floor(Math.random() * shuffledPackages.length);
          } while (newIndex === prevIndex && shuffledPackages.length > 1);
          return newIndex;
        });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [shuffledPackages]);

  const handleQuoteClick = (e) => {
    e.preventDefault();
    setShowPopup(true);
  };

  const currentPackage = shuffledPackages[currentPackageIndex] || null;

  const goToPackage = (index) => {
    setDirection(index > currentPackageIndex ? 1 : -1);
    setCurrentPackageIndex(index);
  };

  return (
    <>
      <Helmet>
        <title>
          Tripocation Holiday Pvt. Ltd. | Explore India & International Travel
        </title>
        <meta
          name="description"
          content="Tripocation Holiday is a trusted travel agency offering India and international tour packages. Explore luxury destinations with expert planning."
        />
        <link rel="canonical" href="https://tripocationholiday.com/" />
        <meta
          property="og:title"
          content="Tripocation Holiday | Explore India & International Travel"
        />
        <meta
          property="og:description"
          content="Discover luxury tour packages and customized holiday with Tripocation. Plan your next adventure with us."
        />
        <meta
          property="og:image"
          content="https://tripocationholiday.com/assets/og-image.jpg"
        />
        <meta property="og:url" content="https://tripocationholiday.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {[...Array(25)].map((_, i) => (
        <Snowflake
          key={i}
          left={Math.random() * 100}
          delay={Math.random() * 5}
          duration={Math.random() * 5 + 5}
        />
      ))}

      {!currentPackage && (
        <div className="min-h-[70vh] flex items-center justify-center text-white text-xl">
          Loading package details...
        </div>
      )}

      {currentPackage && (
        <section
          className="relative h-[70vh] sm:h-[60vh] md:h-[60vh] lg:h-[70vh] xl:h-[80vh] min-h-[400px] mt-28 flex items-center justify-center bg-black overflow-hidden"
          role="banner"
          aria-label="Tripocation Travel Hero Section"
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPackage.id}
              custom={direction}
              initial={{ opacity: 0, x: direction * 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -100 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              style={{ y }}
              className="absolute inset-0 z-0 overflow-hidden"
            >
              <img
                src={currentPackage.image1}
                alt={`${currentPackage.country}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent z-0"
          />

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center">
            <div className="flex-1 flex items-center justify-center w-full px-4">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={`content-${currentPackage.id}`}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -50 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center max-w-6xl w-full space-y-6"
                >
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-2xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white tracking-tight leading-snug sm:leading-tight mb-3 px-2 mt-20 sm:mt-16"
                  >
                    <span className="text-yellow-400 font-extrabold">
                      {["UAE", "Indonesia", "India"].includes(
                        currentPackage.country
                      )
                        ? currentPackage.destination
                        : currentPackage.country}
                    </span>
                  </motion.h1>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 font-semibold border-t border-gray-700/50 pt-3 inline-block"
                  >
                    <span className="text-yellow-400">
                      {currentPackage.numberOfNights} Nights
                    </span>{" "}
                    /{" "}
                    <span className="text-yellow-400">
                      {currentPackage.numberOfDays} Days
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, type: "spring" }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-4 mb-6"
                  >
                    <span
                      className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-yellow-400"
                      style={{ WebkitTextStroke: "0.4px black" }}
                    >
                      {currentPackage.priceNote?.match(/INR [\d,]+/)?.[0] +
                        " /-" ?? ""}
                    </span>
                    <span className="text-xs sm:text-base text-gray-300 bg-gray-800/50 px-3 py-1 rounded-full">
                      Per Person
                    </span>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="w-full pb-12 sm:pb-16 flex justify-center">
              <motion.a
                href="#"
                onClick={handleQuoteClick}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: 1.8,
                }}
                whileHover={{
                  scale: 1.05,
                  y: -4,
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.3)",
                }}
                whileTap={{ scale: 0.98 }}
                className="inline-block bg-yellow-500 text-black px-5 py-2 sm:px-8 sm:py-3 lg:px-10 lg:py-4 rounded-xl
    transition-all duration-300 focus:outline-none 
    focus:ring-2 focus:ring-yellow-300 focus:ring-offset-4
    text-sm sm:text-base lg:text-lg font-semibold
    shadow-lg shadow-yellow-400/40 hover:shadow-xl hover:shadow-yellow-400/50"
                role="button"
                aria-label="Get Free Quote"
              >
                {buttonText}
                <motion.span
                  className="ml-1 sm:ml-2 inline-block"
                  animate={{ x: [0, 4, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    ease: "easeInOut",
                  }}
                >
                  →
                </motion.span>
              </motion.a>
            </div>
          </div>
        </section>
      )}

      <EnquiryPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </>
  );
};

Home.propTypes = {
  buttonText: PropTypes.string,
};

export default Home;
